import clsx from 'clsx';
import { motion } from 'framer-motion';
import { useRef } from 'react';

import { useDoubleClick } from '@repo/utils';

import { getTextRarityClass, tw } from '../../utils';
import Icon from '../Icon';

interface TileButtonProps {
  testId?: string;
  children?: React.ReactNode;
  isSelected: boolean;
  isDisabled?: boolean;
  bgColor?: string;
  onClick: () => void;
  onDoubleClick?: () => void;
  className?: string;
  size?: 'xsmall' | 'small' | 'medium' | 'large' | 'default';
  style?: React.CSSProperties;
  badgeComponent?: React.ReactNode;
  rarity?: string;
  selectedBorderColor?: string;
  unselectedBorderColor?: string;
  showCheckmark?: boolean;
  borderRadius?: string;
  showInitialBorder?: boolean;
  isPublic?: boolean;
}

export const ColorCircleButton: React.FC<TileButtonProps> = ({
  testId,
  children,
  onClick,
  onDoubleClick,
  isSelected = false,
  isDisabled = false,
  bgColor = 'white',
  className,
  size = 'default',
  badgeComponent,
  style,
  rarity,
  selectedBorderColor = 'white',
  showCheckmark = true,
  borderRadius = 'rounded-[18px]',
  showInitialBorder = true,
  isPublic = false,
}: TileButtonProps) => {
  const sizeClassMap = {
    default: 'w-full',
    xsmall: 'w-[35px]',
    small: 'w-[64px]',
    medium: 'w-[100px]',
    large: 'w-[160px]',
  };

  const buttonRef = useRef<HTMLButtonElement>(null);

  useDoubleClick({
    onSingleClick: () => {
      onClick();
    },
    onDoubleClick: () => {
      onDoubleClick && onDoubleClick();
    },
    ref: buttonRef,
    latency: 250,
  });

  return (
    <motion.button
      whileHover={
        (!isPublic && !isDisabled) || !isDisabled
          ? { scale: 1.05 }
          : { scale: 1 }
      }
      whileTap={
        (!isPublic && !isDisabled) || !isDisabled
          ? { scale: 0.95 }
          : { scale: 1 }
      }
      transition={
        (!isPublic && !isDisabled) || !isDisabled
          ? {
              type: 'spring',
              stiffness: 400,
              damping: 10,
            }
          : {}
      }
      className={clsx(
        rarity &&
          (isSelected ? `${getTextRarityClass(rarity)} p-1` : 'bg-white/50'),
        rarity && 'rounded-[18px]',
        rarity && showInitialBorder && 'p-1',
        isPublic && 'hover:cursor-default',
      )}
      data-testid={`tileButton-${testId}`}
      ref={buttonRef}
      disabled={isDisabled}
      style={{
        ...style,
      }}
    >
      <div
        className={tw(
          'relative flex aspect-square items-center justify-center',
          sizeClassMap[size],
          isDisabled ? 'hover:cursor-not-allowed' : '',
          className,
          rarity ? 'rounded-[15px]' : borderRadius,
        )}
        style={{
          ...(rarity
            ? {}
            : {
                ...style,
                border: isSelected
                  ? `4px solid ${selectedBorderColor}`
                  : showInitialBorder
                    ? '4px solid transparent'
                    : '',
              }),
          backgroundColor:
            bgColor === 'white'
              ? isSelected
                ? rarity === 'essential'
                  ? '#e1e1e1'
                  : 'rgba(255, 255, 255, 0.6)'
                : `rgba(255, 255, 255, 0.3)`
              : bgColor,
        }}
      >
        {children}

        {isSelected && showCheckmark ? (
          <div
            className={`absolute flex items-center justify-center rounded-full border-4 bg-white ${
              size === 'small'
                ? '-right-2 -top-2 h-[28px] w-[28px]'
                : '-right-3 -top-3 h-[40px] w-[40px]'
            } `}
            style={{
              borderColor: selectedBorderColor,
            }}
          >
            <Icon
              name='purpleCheckIcon'
              width={20}
              height={20}
              className={size === 'small' ? 'max-h-[12px] max-w-[12px]' : ''}
            />
          </div>
        ) : null}

        {badgeComponent ? badgeComponent : null}
      </div>
    </motion.button>
  );
};
